import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { ALL_SURFACES, type DetectedSurface, type SurfaceAssignment, type SurfaceKind, SURFACE_LABELS } from "./studio";
import type { Segment } from "./studio/segments";
import { rateLimit } from "./spam-protection";

const GATEWAY_URL = "https://ai.gateway.lovable.dev/v1";

// Shrink a normalized polygon inward toward its centroid by `ratio` (0..1).
// Compensates for loose vision-model polygons that would otherwise bleed
// paint onto adjacent floor / furniture / trim pixels.
function shrinkPolygon(poly: [number, number][], ratio: number): [number, number][] {
  if (poly.length < 3) return poly;
  let cx = 0, cy = 0;
  for (const [x, y] of poly) { cx += x; cy += y; }
  cx /= poly.length; cy /= poly.length;
  const k = 1 - Math.max(0, Math.min(0.2, ratio));
  return poly.map(([x, y]) => [
    Math.max(0, Math.min(1, cx + (x - cx) * k)),
    Math.max(0, Math.min(1, cy + (y - cy) * k)),
  ] as [number, number]);
}

// Public studio endpoints have no auth (guests use the visualizer). Guard
// against runaway cost with a shared budget cap plus a per-IP rate limit.
async function guardPublicAi(kind: string, perHour: number) {
  const { assertAiBudget } = await import("@/lib/admin/ai-budget.server");
  await assertAiBudget();
  try {
    const { getRequest } = await import("@tanstack/react-start/server");
    const req = getRequest();
    const fwd = req?.headers.get("x-forwarded-for") || "";
    const ip = fwd.split(",")[0]?.trim() || "unknown";
    const rl = rateLimit(`studio:${kind}:${ip}`, { limit: perHour, windowMs: 60 * 60 * 1000 });
    if (!("ok" in rl) || rl.ok === false) {
      throw new Error("Too many requests — please wait a moment and try again.");
    }
  } catch (e) {
    // Only rethrow rate-limit errors; header parsing failures should not block.
    if ((e as Error).message?.startsWith("Too many")) throw e;
  }
}

// Rough cost estimates in Lovable credits per call. These are deliberately
// approximate — the admin UI surfaces them as "estimated", not billed.
const COST_ESTIMATE = {
  segmentation: 0.3,
  final_render: 4,
  recolor: 4,
} as const;

type LogKind = keyof typeof COST_ESTIMATE;

async function logAiUsage(entry: {
  kind: LogKind;
  model?: string;
  status: "ok" | "error";
  error?: string;
  projectId?: string | null;
  anonToken?: string | null;
  durationMs: number;
}) {
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin.from("ai_usage_logs").insert({
      kind: entry.kind,
      model: entry.model ?? null,
      status: entry.status,
      error: entry.error?.slice(0, 500) ?? null,
      estimated_cost_credits: COST_ESTIMATE[entry.kind],
      project_id: entry.projectId ?? null,
      anon_token: entry.anonToken ?? null,
      duration_ms: entry.durationMs,
    });
  } catch {
    // Logging must never break the user-facing call.
  }
}

async function callGateway(body: unknown) {
  const key = process.env.LOVABLE_API_KEY;
  if (!key) throw new Error("Missing LOVABLE_API_KEY");
  const res = await fetch(`${GATEWAY_URL}/chat/completions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Lovable-API-Key": key,
    },
    body: JSON.stringify(body),
    signal: AbortSignal.timeout(45_000),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    try {
      const { alertAiGatewayFailure } = await import("@/lib/logging/integration-failure.server");
      const model = (body as { model?: string })?.model;
      await alertAiGatewayFailure({ res, bodyText: text, model, route: "studio.functions:callGateway", op: "chat_completions" });
    } catch { /* never block */ }
    throw new Error(`AI Gateway ${res.status}: ${text}`);
  }
  return res.json();
}

/**
 * Detect paintable surfaces in an uploaded photo.
 * Returns a list of named surfaces with brief location descriptions.
 * Masks are not produced here — full pixel segmentation happens at recolor time
 * via the image-edit model. This keeps detection cheap and fast.
 */
export const detectSurfaces = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z.object({ imageUrl: z.string().url() }).parse(input),
  )
  .handler(async ({ data }): Promise<{ surfaces: DetectedSurface[] }> => {
    await guardPublicAi("detect", 30);
    const surfaceList = ALL_SURFACES.map((s) => `"${s}"`).join(", ");
    const result = await callGateway({
      model: "google/gemini-3-flash-preview",
      messages: [
        {
          role: "system",
          content:
            "You are a vision model for an interior/exterior painting visualizer. " +
            "Look at the photo and list every paintable surface you can see. " +
            "Return STRICT JSON only. No prose.",
        },
        {
          role: "user",
          content: [
            {
              type: "text",
              text:
                `Identify paintable surfaces in this photo. Valid surface kinds: ${surfaceList}. ` +
                `For each, write a short description of where it is in the image (e.g. "large back wall behind sofa"). ` +
                `Return JSON of shape: {"surfaces":[{"kind":"walls","description":"...","confidence":0.0-1.0}]}. ` +
                `Only include surfaces you can actually see. Order by visual prominence.`,
            },
            { type: "image_url", image_url: { url: data.imageUrl } },
          ],
        },
      ],
      response_format: { type: "json_object" },
    });
    const content: string = result?.choices?.[0]?.message?.content ?? "{}";
    let parsed: { surfaces?: DetectedSurface[] } = {};
    try {
      parsed = JSON.parse(content);
    } catch {
      parsed = {};
    }
    const valid = (parsed.surfaces ?? []).filter(
      (s) => s && ALL_SURFACES.includes(s.kind as SurfaceKind),
    );
    return { surfaces: valid };
  });

/**
 * Detect paintable surfaces AND return rough polygon outlines per surface.
 * Polygons are normalized 0..1 image coordinates. Multiple disjoint regions
 * are supported (e.g. two visible walls of the same kind become two polygons
 * under one segment).
 */
export const detectSurfaceMasks = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z.object({
      imageUrl: z.string().url(),
      projectId: z.string().uuid().optional(),
      anonToken: z.string().optional(),
    }).parse(input),
  )
  .handler(async ({ data }): Promise<{ segments: Segment[] }> => {
    const t0 = Date.now();
    // Two-pass pipeline:
    //  Pass A (structural): classify the scene AND outline large paintable
    //    regions with the fast/cheap flash model. Every scene runs this pass.
    //  Pass B (trim/detail): only when the scene is exterior, run a stronger
    //    Gemini pro pass that focuses on narrow trim, fascia, soffit,
    //    shutters, and per-side wall separation.
    // Both passes return polygons in normalized 0..1 image coords and are
    // merged into one Segment[] with per-segment source + confidence flags.
    // Trim segments naturally have smaller area than walls, so the existing
    // hitTest (smallest-area wins) gives them click priority automatically.
    const structuralModel = "google/gemini-3-flash-preview";
    const trimModel = "google/gemini-2.5-pro";
    try {
    const { assertAiBudget } = await import("@/lib/admin/ai-budget.server");
    await assertAiBudget();

    const structural = await runStructuralPass(structuralModel, data.imageUrl);
    let trim: Segment[] = [];
    if (structural.scene === "exterior") {
      try {
        trim = await runTrimPass(trimModel, data.imageUrl);
      } catch (e) {
        // Trim pass is best-effort — a failure must not lose the structural pass.
        // eslint-disable-next-line no-console
        console.warn("[studio] trim pass failed:", (e as Error).message);
      }
    }
    const merged = mergeSegments(structural.segments, trim);
    await logAiUsage({
      kind: "segmentation",
      model: trim.length ? `${structuralModel}+${trimModel}` : structuralModel,
      status: "ok",
      projectId: data.projectId,
      anonToken: data.anonToken,
      durationMs: Date.now() - t0,
    });
    return { segments: merged };
    } catch (e) {
      await logAiUsage({ kind: "segmentation", model: structuralModel, status: "error", error: (e as Error).message, projectId: data.projectId, anonToken: data.anonToken, durationMs: Date.now() - t0 });
      throw e;
    }
  });

type Scene = "interior" | "exterior" | "cabinets" | "deck" | "mixed";

const EXTERIOR_KINDS: SurfaceKind[] = ["siding", "brick", "trim", "doors", "windows", "garage-doors", "shutters", "fence", "deck"];

function polygonsFromRaw(raw: unknown): [number, number][][] {
  if (!Array.isArray(raw)) return [];
  const polys = raw.filter(
    (p) => Array.isArray(p) && p.length >= 3 && p.every((pt) => Array.isArray(pt) && pt.length === 2),
  ) as [number, number][][];
  return polys.map((poly) =>
    poly.map(([x, y]) => [Math.max(0, Math.min(1, x)), Math.max(0, Math.min(1, y))] as [number, number]),
  );
}

function parseSegmentsPayload(content: string, source: "structural" | "trim"): Segment[] {
  let parsed: { segments?: Array<{ id?: string; kind?: string; label?: string; polygons?: unknown; confidence?: number }> } = {};
  try { parsed = JSON.parse(content); } catch { parsed = {}; }
  const out: Segment[] = [];
  for (const s of parsed.segments ?? []) {
    if (!s || !ALL_SURFACES.includes(s.kind as SurfaceKind)) continue;
    const polys = polygonsFromRaw(s.polygons);
    if (polys.length === 0) continue;
    // Trim strokes are narrow — shrinking them destroys the mask. Only shrink
    // large structural polygons to avoid bleeding into adjacent surfaces.
    const shrink = source === "trim" ? 0 : 0.012;
    const cleaned = polys
      .map((p) => (shrink > 0 ? shrinkPolygon(p, shrink) : p))
      .filter((p) => p.length >= 3);
    if (cleaned.length === 0) continue;
    const confidence = typeof s.confidence === "number" ? Math.max(0, Math.min(1, s.confidence)) : 0.7;
    const reviewStatus: Segment["reviewStatus"] =
      confidence >= 0.75 ? "high" : confidence >= 0.5 ? "review" : "low";
    out.push({
      id: String(s.id ?? crypto.randomUUID()),
      kind: s.kind as SurfaceKind,
      label: String(s.label ?? SURFACE_LABELS[s.kind as SurfaceKind] ?? s.kind).slice(0, 60),
      polygons: cleaned,
      confidence,
      reviewStatus,
      source,
    });
  }
  return out;
}

async function runStructuralPass(
  model: string,
  imageUrl: string,
): Promise<{ scene: Scene; segments: Segment[] }> {
  const surfaceList = ALL_SURFACES.map((s) => `"${s}"`).join(", ");
  const result = await callGateway({
    model,
    messages: [
      {
        role: "system",
        content:
          "You are a vision model for an interior/exterior painting visualizer. " +
          "First classify the scene, then outline every LARGE paintable architectural " +
          "surface as tight polygons in normalized 0..1 image coords (origin top-left). " +
          "You MUST separate individual wall/facade planes even when they share the same " +
          "material and color — front vs. side of a house, front wall vs. gable, front " +
          "wall vs. recessed entry wall are ALWAYS separate segments. " +
          "Never include pixels belonging to furniture, decor, windows, glass, roofing, " +
          "sky, trees, grass, driveways, sidewalks, vehicles, people, or hardware. " +
          "Return STRICT JSON only.",
      },
      {
        role: "user",
        content: [
          {
            type: "text",
            text:
              `Analyze this photograph.\n\n` +
              `1) Classify the scene as one of: "interior", "exterior", "cabinets", "deck", "mixed".\n` +
              `2) Outline every LARGE paintable architectural surface. Valid surface kinds: ${surfaceList}.\n\n` +
              `INSTANCE SEPARATION RULES — return each of these as its OWN segment, never merged:\n` +
              `- Each visible facade plane (front wall, left wall, right wall, recessed entry wall).\n` +
              `- Each gable and each dormer.\n` +
              `- Front-facing garage wall vs. side garage wall.\n` +
              `- Porch walls vs. porch ceiling.\n` +
              `- Each interior wall visible in the photo (front wall, left wall, right wall, accent wall).\n` +
              `- Ceiling vs. any wall.\n` +
              `- Each cabinet bank.\n\n` +
              `LABELS: use short human-friendly names ("Main Front Siding", "Left Gable", ` +
              `"Recessed Entry Wall", "Porch Ceiling", "Front Wall", "Accent Wall"). ` +
              `Never emit "segment_1", "mask_3", or model-internal names.\n\n` +
              `EXCLUSIONS — never cover: windows/glass, roof shingles/metal, sky, trees, grass, ` +
              `sidewalks, driveways, vehicles, gutters, downspouts, light fixtures, house numbers, ` +
              `door hardware, AC units, utility boxes, furniture, textiles, rugs, curtains, ` +
              `bedding, appliances, people, pets. Cut polygons around foreground objects.\n\n` +
              `IGNORE thin trim, fascia, soffit, corner boards, and shutters in this pass — a ` +
              `separate detail pass handles them. Focus on large paintable planes.\n\n` +
              `Each polygon may have 8–40 vertices. Prefer several smaller polygons under one ` +
              `segment over one loose polygon that swallows non-paintable objects. When uncertain ` +
              `about a boundary, EXCLUDE the pixel.\n\n` +
              `Include a confidence (0..1) per segment reflecting how sure you are of the boundary.\n\n` +
              `Return JSON of shape:\n` +
              `{"scene":"exterior","segments":[{"id":"seg1","kind":"siding","label":"Main Front Siding","confidence":0.9,"polygons":[[[0.1,0.3],[0.5,0.28],[0.52,0.72],[0.08,0.74]]]}]}`,
          },
          { type: "image_url", image_url: { url: imageUrl } },
        ],
      },
    ],
    response_format: { type: "json_object" },
  });
  const content: string = result?.choices?.[0]?.message?.content ?? "{}";
  let scene: Scene = "mixed";
  try {
    const raw = JSON.parse(content) as { scene?: string };
    if (raw.scene && ["interior", "exterior", "cabinets", "deck", "mixed"].includes(raw.scene)) {
      scene = raw.scene as Scene;
    }
  } catch { /* keep default */ }
  const segments = parseSegmentsPayload(content, "structural");
  // Guardrail: if the model returned only interior kinds, don't run the exterior trim pass.
  if (scene === "exterior" && segments.length > 0) {
    const anyExterior = segments.some((s) => EXTERIOR_KINDS.includes(s.kind));
    if (!anyExterior) scene = "mixed";
  }
  return { scene, segments };
}

async function runTrimPass(model: string, imageUrl: string): Promise<Segment[]> {
  const result = await callGateway({
    model,
    messages: [
      {
        role: "system",
        content:
          "You are a vision model specialised in NARROW exterior architectural details. " +
          "Your only job is to outline trim, fascia, soffit, corner boards, window trim, " +
          "door trim, shutters, garage doors, and garage trim as tight polygons in " +
          "normalized 0..1 image coords. Do NOT outline large siding walls, brick, sky, " +
          "roof, landscaping, or glass. Return STRICT JSON only.",
      },
      {
        role: "user",
        content: [
          {
            type: "text",
            text:
              `Outline ONLY the following narrow exterior surfaces:\n` +
              `- Window trim (per window, or grouped per side of the house)\n` +
              `- Door trim (separate from the door slab)\n` +
              `- Front door slab (kind:"doors")\n` +
              `- Garage door slab (kind:"garage-doors")\n` +
              `- Garage trim (kind:"trim", label:"Garage Trim")\n` +
              `- Fascia along rooflines (kind:"trim", label:"Front Fascia" etc.)\n` +
              `- Soffit under eaves (kind:"trim", label:"Front Soffit" etc.)\n` +
              `- Corner boards / rake boards / frieze boards (kind:"trim")\n` +
              `- Shutters (kind:"shutters")\n\n` +
              `HARD EXCLUSIONS: no siding fields, no brick walls, no sky, no roof shingles, ` +
              `no windows/glass, no gutters, no downspouts, no landscaping, no driveway, ` +
              `no vehicles, no house numbers, no light fixtures, no door hardware.\n\n` +
              `Trim is often narrow — trace it TIGHT. It is fine to emit a long thin polygon ` +
              `with many vertices along a roofline. Do NOT expand trim outward to include ` +
              `adjacent siding or glass. When trim and siding are similar colors, still detect ` +
              `the trim using its architectural edges.\n\n` +
              `Use human-friendly labels ("Front Window Trim", "Front Door Trim", ` +
              `"Front Fascia", "Left Soffit", "Left Shutters", "Front Corner Boards", ` +
              `"Garage Door", "Garage Trim"). Never emit "segment_1" or "trim_2".\n\n` +
              `Include a confidence (0..1) per segment. Mark low confidence (<0.6) when the ` +
              `boundary is partly obscured by shadow or foliage.\n\n` +
              `Valid kinds are exactly: "trim", "doors", "garage-doors", "shutters", ` +
              `"windows" (only if you are outlining a painted window frame, NEVER the glass).\n\n` +
              `Return JSON of shape:\n` +
              `{"segments":[{"id":"trim1","kind":"trim","label":"Front Fascia","confidence":0.85,"polygons":[[[0.05,0.12],[0.95,0.11],[0.95,0.14],[0.05,0.15]]]}]}`,
          },
          { type: "image_url", image_url: { url: imageUrl } },
        ],
      },
    ],
    response_format: { type: "json_object" },
  });
  const content: string = result?.choices?.[0]?.message?.content ?? "{}";
  const segs = parseSegmentsPayload(content, "trim");
  // Trim pass should only ever emit these narrow-detail kinds.
  const allowed = new Set<SurfaceKind>(["trim", "doors", "garage-doors", "shutters", "windows"]);
  return segs.filter((s) => allowed.has(s.kind));
}

function mergeSegments(structural: Segment[], trim: Segment[]): Segment[] {
  // Ensure ids are unique across passes (trim pass may reuse "seg1"-style ids).
  const seen = new Set<string>();
  const dedupe = (s: Segment): Segment => {
    let id = s.id;
    while (seen.has(id)) id = `${id}_${Math.random().toString(36).slice(2, 6)}`;
    seen.add(id);
    return { ...s, id };
  };
  // Structural segments first (walls, siding) so they render underneath;
  // trim segments come after so their smaller area gives them click priority
  // via the existing smallest-area hitTest.
  return [...structural.map(dedupe), ...trim.map(dedupe)];
}

/**
 * Recolor a photo by applying colors to specific surfaces.
 * Uses Gemini image-edit so lighting, shadows, reflections and texture are preserved
 * (the model edits the source image rather than regenerating from scratch).
 * Returns a base64 data URL the caller can persist to storage.
 */
export const recolorImage = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        imageUrl: z.string().url(),
        assignments: z
          .array(
            z.object({
              kind: z.string(),
              colorId: z.string(),
              colorName: z.string(),
              colorNumber: z.string(),
              brand: z.string(),
              hex: z.string(),
            }),
          )
          .min(1),
      })
      .parse(input),
  )
  .handler(async ({ data }): Promise<{ dataUrl: string }> => {
    await guardPublicAi("recolor", 15);
    const key = process.env.LOVABLE_API_KEY;
    if (!key) throw new Error("Missing LOVABLE_API_KEY");

    const lines = (data.assignments as SurfaceAssignment[]).map(
      (a) =>
        `• Paint the ${SURFACE_LABELS[a.kind as SurfaceKind] ?? a.kind} in ${a.brand} "${a.colorName}" (${a.colorNumber}), hex ${a.hex}.`,
    );
    const instruction =
      `Carefully repaint the following surfaces in this photo. ` +
      `CRITICAL: preserve the exact lighting, shadows, reflections, texture, perspective, ` +
      `and every other object exactly as it appears. Do not change the composition, ` +
      `furniture, decor, or any non-painted surface. Only change the paint color of:\n` +
      lines.join("\n");

    const res = await fetch(`${GATEWAY_URL}/chat/completions`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Lovable-API-Key": key },
      body: JSON.stringify({
        model: "google/gemini-3.1-flash-image",
        messages: [
          {
            role: "user",
            content: [
              { type: "text", text: instruction },
              { type: "image_url", image_url: { url: data.imageUrl } },
            ],
          },
        ],
        modalities: ["image", "text"],
      }),
      signal: AbortSignal.timeout(90_000),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`AI Gateway ${res.status}: ${text}`);
    }
    const json = await res.json();
    // OpenRouter chat-completions image shape: choices[0].message.images[0].image_url.url
    // (data URL with base64 PNG)
    const url: string | undefined =
      json?.choices?.[0]?.message?.images?.[0]?.image_url?.url;
    if (!url) {
      throw new Error("No image returned from model");
    }
    return { dataUrl: url };
  });

/**
 * Photorealistic refinement of a locally-rendered preview. The caller passes the
 * EXACT preview image produced by the in-browser canvas (which already has the
 * right colors on the right surfaces). Gemini's only job is to make it look like
 * a real photo — sharpen edges, reconcile reflections — WITHOUT changing which
 * surface is which color. This avoids the "wrong wall got the wrong color"
 * problem you get when you ask the model to figure out segmentation itself.
 */
export const refinePreview = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        previewImageUrl: z.string(), // data URL or http URL
        sourceImageUrl: z.string().url(),
        palette: z
          .array(z.object({
            label: z.string(),
            colorName: z.string(),
            colorNumber: z.string(),
            hex: z.string(),
          }))
          .min(1),
        projectId: z.string().uuid().optional(),
        anonToken: z.string().optional(),
      })
      .parse(input),
  )
  .handler(async ({ data }): Promise<{ dataUrl: string }> => {
    const t0 = Date.now();
    const model = "google/gemini-3.1-flash-image";
    try {
    const { assertAiBudget } = await import("@/lib/admin/ai-budget.server");
    await assertAiBudget();
    const key = process.env.LOVABLE_API_KEY;
    if (!key) throw new Error("Missing LOVABLE_API_KEY");

    const paletteLines = data.palette
      .map((p) => `• ${p.label}: ${p.colorName} (${p.colorNumber}), hex ${p.hex}`)
      .join("\n");

    const instruction =
      `You are given TWO images:\n` +
      `1) A COLORED PREVIEW — this already shows the EXACT color on the EXACT surface the user wants. ` +
      `Treat the color layout as ground truth.\n` +
      `2) An ORIGINAL PHOTO — reference for lighting, shadows, reflections, texture, and detail fidelity.\n\n` +
      `Produce a photorealistic version of the COLORED PREVIEW. Keep every surface's color ` +
      `matching the preview (use these reference hex values):\n${paletteLines}\n\n` +
      `CRITICAL RULES:\n` +
      `- Do NOT move colors between surfaces. The preview defines which surface has which color.\n` +
      `- Do NOT recolor any surface that was left unchanged in the preview.\n` +
      `- Restore photographic realism: natural shadows, soft edges, real wall texture, real reflections from the original photo.\n` +
      `- Preserve composition, furniture, decor, perspective, and every non-painted object exactly.\n` +
      `- Output a single image at the same dimensions as the preview.`;

    const res = await fetch(`${GATEWAY_URL}/chat/completions`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Lovable-API-Key": key },
      body: JSON.stringify({
        model,
        messages: [
          {
            role: "user",
            content: [
              { type: "text", text: instruction },
              { type: "image_url", image_url: { url: data.previewImageUrl } },
              { type: "image_url", image_url: { url: data.sourceImageUrl } },
            ],
          },
        ],
        modalities: ["image", "text"],
      }),
      signal: AbortSignal.timeout(120_000),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`AI Gateway ${res.status}: ${text}`);
    }
    const json = await res.json();
    const url: string | undefined =
      json?.choices?.[0]?.message?.images?.[0]?.image_url?.url;
    if (!url) throw new Error("No image returned from model");
    await logAiUsage({ kind: "final_render", model, status: "ok", projectId: data.projectId, anonToken: data.anonToken, durationMs: Date.now() - t0 });
    return { dataUrl: url };
    } catch (e) {
      await logAiUsage({ kind: "final_render", model, status: "error", error: (e as Error).message, projectId: data.projectId, anonToken: data.anonToken, durationMs: Date.now() - t0 });
      throw e;
    }
  });